import { toPng, toSvg } from 'html-to-image';
import { saveAs } from 'file-saver';

export interface ExportOptions {
  fileName?: string;
  includeBackground?: boolean;
  transparent?: boolean;
  scale?: number; // 1x, 2x, 4x for PNG
}

// Export canvas as PNG
export const exportAsPNG = async (
  element: HTMLElement,
  options: ExportOptions = {}
): Promise<void> => {
  const {
    fileName = 'architecture.png',
    includeBackground = true,
    transparent = false,
    scale = 2
  } = options;

  try {
    const backgroundColor = transparent ? 'transparent' : includeBackground ? '#ffffff' : 'transparent';
    
    const dataUrl = await toPng(element, {
      backgroundColor,
      pixelRatio: scale,
      filter: (node) => {
        // Exclude controls, panels, and other UI elements
        if (node instanceof HTMLElement) {
          // Exclude React Flow controls and panels
          if (node.classList?.contains('react-flow__controls')) return false;
          if (node.classList?.contains('react-flow__panel')) return false;
          if (node.classList?.contains('react-flow__attribution')) return false;
          // Keep the background and nodes/edges
          return true;
        }
        return true;
      }
    });

    // Convert data URL to blob and download
    const response = await fetch(dataUrl);
    const blob = await response.blob();
    saveAs(blob, fileName);
  } catch (error) {
    console.error('Failed to export PNG:', error);
    throw error;
  }
};

// Export canvas as SVG
export const exportAsSVG = async (
  element: HTMLElement,
  options: ExportOptions = {}
): Promise<void> => {
  const {
    fileName = 'architecture.svg',
    includeBackground = true,
    transparent = false
  } = options;

  try {
    const backgroundColor = transparent ? 'transparent' : includeBackground ? '#ffffff' : 'transparent';
    
    const dataUrl = await toSvg(element, {
      backgroundColor,
      filter: (node) => {
        // Exclude controls, panels, and other UI elements
        if (node instanceof HTMLElement) {
          if (node.classList?.contains('react-flow__controls')) return false;
          if (node.classList?.contains('react-flow__panel')) return false;
          if (node.classList?.contains('react-flow__attribution')) return false;
          return true;
        }
        return true;
      }
    });

    // Convert data URL to blob and download
    const response = await fetch(dataUrl);
    const blob = await response.blob();
    saveAs(blob, fileName);
  } catch (error) {
    console.error('Failed to export SVG:', error);
    throw error;
  }
};

// Generate thumbnail for project preview
export const generateThumbnail = async (
  element: HTMLElement,
  maxWidth: number = 400,
  maxHeight: number = 300
): Promise<string> => {
  try {
    const dataUrl = await toPng(element, {
      backgroundColor: '#ffffff',
      pixelRatio: 1,
      width: maxWidth,
      height: maxHeight,
      style: {
        transform: 'scale(0.5)',
        transformOrigin: 'top left'
      },
      filter: (node) => {
        if (node instanceof HTMLElement) {
          if (node.classList?.contains('react-flow__controls')) return false;
          if (node.classList?.contains('react-flow__panel')) return false;
          if (node.classList?.contains('react-flow__attribution')) return false;
          return true;
        }
        return true;
      }
    });

    return dataUrl;
  } catch (error) {
    console.error('Failed to generate thumbnail:', error);
    return '';
  }
};
